import { useState, useMemo } from "react";
import Layout from "../../components/global/Layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faSearch, faTh, faTable, faDownload, faFileCsv, faCloudDownloadAlt, faBookOpen, faStar, faBookmark } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import CardSkeleton from "../../components/global/card/CardSkeleton/CardSkeleton";
import CardBook from "../../components/global/card/CardBook/CardBook";
import Paginator from "react-hooks-paginator";
import { fetcher } from "../../utils/fetcher";
import useSWR from "swr";

const HasilPenilaian = () => {
  const [filters, setFilters] = useState({
    type: "btpcatalogue",
    title: "",
    author: "",
    publisher: "",
    sk_number: "",
  });

  const [viewMode, setViewMode] = useState("card");
  const [sortConfig, setSortConfig] = useState({ column: null, direction: "asc" });

  const [activeFilters, setActiveFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(15);
  const [offset, setOffset] = useState(0);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    if (name === "type") {
      setCurrentPage(1);
      const newActiveFilters = {
        ...newFilters,
        title: "",
        author: "",
        publisher: "",
        sk_number: "",
      };
      setFilters(newActiveFilters);
      setActiveFilters(newActiveFilters);
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    setActiveFilters(filters);
  };

  const endpointUrl = useMemo(() => {
    const BASE_URL = `https://api.buku.kemendikdasmen.go.id/${activeFilters.type}/book`;
    const params = new URLSearchParams({
      page: currentPage,
      limit: pageLimit,
    });

    const { type, ...otherFilters } = activeFilters;

    Object.keys(otherFilters).forEach((key) => {
      if (otherFilters[key]) {
        params.append(key, otherFilters[key]);
      }
    });

    return `${BASE_URL}?${params.toString()}`;
  }, [activeFilters, currentPage]);

  const { data, error, isLoading } = useSWR(endpointUrl, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  const books = data?.data || [];
  const totalRecords = data?.pagination?.total_pages || 0;

  const scrollTop = () => {
    document.getElementById("catalog")?.scrollIntoView();
  };

  // Scroll ketika currentPage berubah
  useMemo(() => {
    scrollTop();
  }, [currentPage]);

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPricesDisplay = (bookPrices) => {
    if (!bookPrices || Object.keys(bookPrices).length === 0) {
      return "-";
    }
    
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        {Object.values(bookPrices).map((priceObj, index) => (
          <div key={priceObj.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {index > 0 && <span style={{ color: '#ccc' }}>|</span>}
            <span className="badge bg-light text-dark">Zona {priceObj.zone}</span>
            <span className="fw-semibold text-primary" style={{ fontSize: '0.9rem' }}>{formatRupiah(priceObj.price)}</span>
          </div>
        ))}
      </div>
    );
  };

  const getAllZones = () => {
    const zones = new Set();
    books.forEach((book) => {
      if (book.book_prices) {
        Object.values(book.book_prices).forEach((priceObj) => {
          zones.add(priceObj.zone);
        });
      }
    });
    return Array.from(zones).sort((a, b) => parseInt(a) - parseInt(b));
  };

  const getPriceForZone = (bookPrices, zone) => {
    if (!bookPrices) return "-";
    const priceObj = Object.values(bookPrices).find((p) => p.zone === zone);
    return priceObj ? formatRupiah(priceObj.price) : "-";
  };

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.column === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ column, direction });
  };

  const getSortedBooks = () => {
    if (!sortConfig.column) return books;

    const sorted = [...books].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.column) {
        case "title":
          aValue = a.title?.toLowerCase() || "";
          bValue = b.title?.toLowerCase() || "";
          break;
        case "author":
          aValue = a.author?.toLowerCase() || "";
          bValue = b.author?.toLowerCase() || "";
          break;
        case "publisher":
          aValue = a.publisher?.toLowerCase() || "";
          bValue = b.publisher?.toLowerCase() || "";
          break;
        case "level":
          aValue = a.school_level?.name?.toLowerCase() || "";
          bValue = b.school_level?.name?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortConfig.column !== column) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const exportToCSV = () => {
    if (books.length === 0) {
      alert("Tidak ada data untuk diunduh");
      return;
    }

    // Get all zones
    const zones = getAllZones();

    // Prepare CSV headers dengan zona
    const headers = ["Judul Buku", "Penulis", "Penerbit", "Tingkat Sekolah", ...zones.map((zone) => `Zona ${zone}`)];
    
    // Prepare rows dengan data harga
    const rows = books.map((book) => [
      book.title,
      book.author || "-",
      book.publisher || "-",
      book.school_level?.name || "-",
      ...zones.map((zone) => {
        if (!book.book_prices) return "-";
        const priceObj = Object.values(book.book_prices).find((p) => p.zone === zone);
        return priceObj ? priceObj.price : "-";
      }),
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`) // Escape quotes
          .join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const fileName = `hasil-penilaian-${new Date().toISOString().split("T")[0]}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <section id="catalog">
        <div className="container px-3 py-5">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="row">
                <div className="col-12 col-lg-12 position-relative">
                  <h3 className="mb-3">Hasil Penilaian</h3>
                  <p>
                    Mari bersama menghadirkan{" "}
                    <span className="fw-bold">
                    {filters.type === "btpcatalogue" ? "Buku Teks Pendamping" : filters.type === "btucatalogue" ? "Buku Teks Utama" : "Buku Nonteks Pelajaran"}
                    </span>{" "}
                    yang lebih baik melalui buku-buku berkualitas, legal, dan
                    bermutu.
                  </p>
                  <div className="border border-1 rounded p-3">
                    <div className="row g-3">
                      <div className="col-md-2">
                        <label
                          htmlFor="type"
                          className="form-label fw-semibold small"
                        >
                          Tipe Buku
                        </label>
                        <select
                          id="type"
                          name="type"
                          className="form-select"
                          value={filters.type}
                          onChange={handleFilterChange}
                        >
                          <option value="btpcatalogue">BTP</option>
                          <option value="bntpcatalogue">BNT</option>
                          <option value="btucatalogue">BTU</option>
                        </select>
                      </div>

                      <div className="col-md-3">
                        <label
                          htmlFor="title"
                          className="form-label fw-semibold small"
                        >
                          Judul
                        </label>
                        <input
                          id="title"
                          name="title"
                          value={filters.title}
                          onChange={handleFilterChange}
                          type="text"
                          className="form-control"
                          placeholder="Cth: Buku Siswa"
                        />
                      </div>

                      <div className="col-md-3">
                        <label
                          htmlFor="author"
                          className="form-label fw-semibold small"
                        >
                          Penulis
                        </label>
                        <input
                          id="author"
                          name="author"
                          value={filters.author}
                          onChange={handleFilterChange}
                          type="text"
                          className="form-control"
                          placeholder="Cth: Sardianto"
                        />
                      </div>

                      <div className="col-md-2">
                        <label
                          htmlFor="publisher"
                          className="form-label fw-semibold small"
                        >
                          Penerbit
                        </label>
                        <input
                          id="publisher"
                          name="publisher"
                          value={filters.publisher}
                          onChange={handleFilterChange}
                          type="text"
                          className="form-control"
                          placeholder="Cth: Thursina"
                        />
                      </div>

                      <div className="col-md-2">
                        <label
                          htmlFor="sk_number"
                          className="form-label fw-semibold small"
                        >
                          SK Kelayakan
                        </label>
                        <input
                          id="sk_number"
                          name="sk_number"
                          value={filters.sk_number}
                          onChange={handleFilterChange}
                          type="text"
                          className="form-control"
                          placeholder="Cth: 044/H/P/2024"
                        />
                      </div>
                    </div>

                    <div className="d-flex align-items-end justify-content-between mt-3">
                      <div><b>Penting</b>: Pelajari tentang 5 zonasi harga <a role="button" className="" data-bs-toggle="modal" data-bs-target="#zonaModal">disini</a></div>
                      <div className="d-flex gap-2">
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className={`btn ${viewMode === "card" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setViewMode("card")}
                            title="Tampilan Kartu"
                          >
                            <FontAwesomeIcon icon={faTh} /> Kartu
                          </button>
                          <button
                            type="button"
                            className={`btn ${viewMode === "table" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setViewMode("table")}
                            title="Tampilan Tabel"
                          >
                            <FontAwesomeIcon icon={faTable} /> Tabel
                          </button>
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSearchClick}
                        >
                          <FontAwesomeIcon icon={faSearch} /> Cari Buku
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={exportToCSV}
                          title="Unduh data yang ditampilkan"
                        >
                          <FontAwesomeIcon icon={faFileCsv} /> Unduh
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#unduhSemuaModal"
                          title="Unduh semua data"
                        >
                          <FontAwesomeIcon icon={faCloudDownloadAlt} /> Unduh Semua
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="alert alert-warning alert-dismissible fade show mt-3" role="alert">
                <small>
                  <strong>⚠️ Informasi:</strong> Beberapa buku mungkin belum menampilkan sampul karena sedang dalam proses sinkronisasi data. Hal ini tidak mempengaruhi proses pembelian atau ketersediaan buku.
                </small>
                <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
              </div>
              <div className="row">
                {isLoading ? (
                  viewMode === "card" ? (
                    [...Array(9)].map((item, index) => (
                      <div key={index} className="col-lg-4 my-2">
                        <CardSkeleton />
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="table-responsive">
                        <table className="table table-placeholder">
                          <tbody>
                            {[...Array(5)].map((item, index) => (
                              <tr key={index}>
                                <td><div className="placeholder col-12"></div></td>
                                <td><div className="placeholder col-12"></div></td>
                                <td><div className="placeholder col-12"></div></td>
                                <td><div className="placeholder col-12"></div></td>
                                <td><div className="placeholder col-12"></div></td>
                                <td><div className="placeholder col-12"></div></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ) : books.length === 0 ? (
                  <div className="text-center mt-5">
                    <img
                      width="60"
                      src="/assets/image/catalog/not-found.png"
                      className="img-fluid"
                      alt="Not found"
                    />
                    <div className="text-center mt-2">
                      Buku tidak ditemukan atau belum tersedia
                    </div>
                  </div>
                ) : viewMode === "card" ? (
                  books.map((book, index) => (
                    <div key={index} className="col-lg-4 my-2">
                      <Link
                        to={`/hasil-penilaian/${book.id}?type=${filters.type}`}
                        className="text-decoration-none text-dark"
                      >
                        <CardBook
                          image={book.cover_image}
                          title={book.title}
                          typeBook={"pdf"}
                          level={book.school_level?.name || book.level}
                          book={book}
                        />
                      </Link>
                    </div>
                  ))
                ) : (
                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th 
                              style={{ width: "25%", fontSize: '0.95rem', fontWeight: '600', color: '#000', cursor: 'pointer' }}
                              onClick={() => handleSort("title")}
                            >
                              Judul Buku{getSortIcon("title")}
                            </th>
                            <th 
                              style={{ width: "12%", fontSize: '0.95rem', fontWeight: '600', color: '#000', cursor: 'pointer' }}
                              onClick={() => handleSort("author")}
                            >
                              Penulis{getSortIcon("author")}
                            </th>
                            <th 
                              style={{ width: "12%", fontSize: '0.95rem', fontWeight: '600', color: '#000', cursor: 'pointer' }}
                              onClick={() => handleSort("publisher")}
                            >
                              Penerbit{getSortIcon("publisher")}
                            </th>
                            <th 
                              style={{ width: "12%", fontSize: '0.95rem', fontWeight: '600', color: '#000', cursor: 'pointer' }}
                              onClick={() => handleSort("level")}
                            >
                              Tingkat Sekolah{getSortIcon("level")}
                            </th>
                            {getAllZones().map((zone) => (
                              <th key={zone} style={{ width: "8%", fontSize: '0.95rem', fontWeight: '600', color: '#000', textAlign: 'center' }}>
                                Zona {zone}
                              </th>
                            ))}
                            <th style={{ width: "16%", fontSize: '0.95rem', fontWeight: '600', color: '#000' }}>Opsi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getSortedBooks().map((book, index) => (
                            <tr key={index}>
                              <td>
                                <Link
                                  to={`/hasil-penilaian/${book.id}?type=${filters.type}`}
                                  className="text-decoration-none text-dark fw-semibold"
                                  style={{ fontSize: '0.95rem' }}
                                >
                                  {book.title}
                                </Link>
                              </td>
                              <td className="text-dark" style={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>{book.author || "-"}</td>
                              <td>
                                <a
                                  role="button"
                                  className="text-decoration-none text-dark"
                                  style={{ fontSize: '0.9rem', cursor: 'pointer' }}
                                  onClick={() => {
                                    setFilters({ ...filters, publisher: book?.publisher });
                                    setActiveFilters({ ...filters, publisher: book?.publisher });
                                  }}
                                >
                                  {book?.publisher}
                                </a>
                              </td>
                              <td className="text-dark" style={{ fontSize: '0.9rem' }}>{book.school_level?.name || "-"}</td>
                              {getAllZones().map((zone) => (
                                <td key={zone} className="text-primary fw-semibold" style={{ fontSize: '0.9rem', textAlign: 'center' }}>
                                  {getPriceForZone(book.book_prices, zone)}
                                </td>
                              ))}
                              <td>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                                  <Link target="_blank"
                                    to={`/hasil-penilaian/${book.id}?type=${filters.type}`}
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    Detail
                                  </Link>
                                  <a
                                    href={`https://search.siplah.kemendikdasmen.go.id/search?keyword=${encodeURIComponent(book.title)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-success"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    Beli
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <Paginator
                  pagePrevText={<FontAwesomeIcon icon={faArrowLeft} />}
                  pageNextText={<FontAwesomeIcon icon={faArrowRight} />}
                  totalRecords={totalRecords}
                  pageLimit={pageLimit}
                  pageNeighbours={2}
                  setOffset={setOffset}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
              <div className="modal fade" id="zonaModal" data-bs-backdrop="static">
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="exampleModalLabel">Keterangan Zona</h5>
                            {/* <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button> */}
                            <button type="button" className="btn btn-sm btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#hetModal">Kembali</button>
                        </div>
                        <div className="modal-body">
                            <table className="table table-striped">
                                <tbody>
                                    <tr>
                                        <td width={80}>Zona 1</td>
                                        <td>Jawa Barat, Jawa Tengah, Jawa Timur, Daerah Istimewa Yogyakarta, DKI Jakarta, dan Banten</td>
                                    </tr>
                                    <tr>
                                        <td>Zona 2</td>
                                        <td>Bali, Nusa Tenggara Barat, Lampung (kecuali (i) Pesisir Barat), dan Sumatera Selatan.</td>
                                    </tr>
                                    <tr>
                                        <td>Zona 3</td>
                                        <td>Bengkulu, Jambi, Bangka Belitung,  Sumatera  Barat  (kecuali  (i)  Kepulauan  Mentawai  dan  (ii)  Solok Selatan), Riau (kecuali (i) Bengkalis, (ii) Kepulauan Meranti, (iii) , Sumatera Utara (kecuali (i) Nias, (ii) Nias Selatan, (iii) Nias Utara, (iv) Nias Barat), Sulawesi Selatan, Sulawesi Utara (kecuali (i) Kepulauan Sangihe, dan (ii) Kepulauan Talaud), Sulawesi Tengah (kecuali  (i)  Banggai  Kepulauan,  (ii)  Tojo  Uno- Uno, (iii) Sigi, dan (iv) Banggai Laut), Sulawesi Barat, Sulawesi Tenggara (Kecuali (i) Konawe,  (ii) Bombana, dan (iii) Konawe Kepulauan), dan Gorontalo.</td>
                                    </tr>
                                    <tr>
                                        <td>Zona 4</td>
                                        <td>Nanggroe Aceh Darussalam (kecuali (i) Aceh Besar dan (ii) Aceh Singkil), Kepulauan Riau (kecuali (i) Karimun, (ii)  Kepulauan Anambas, dan (iii)  Natuna), Nusa  Tenggara  Timur (kecuali (i) Sumba Barat, (ii) Sumba  Timur,  (iii)  Timor  Tengah  Selatan,  (iv)  Belu,  (v)  Alor,  (vi)  Lembata,  (vii)  Ende,  (viii) Manggarai,   (ix)   Rote   Ndao,   (x)   Manggarai   Barat,   (xi)   Sumba   Tengah,   (xii)   Sumba   Barat   Daya,   (xiii) Nagekeo, (xiv) Manggarai Timur, (xv) Sabu Raijua, dan (xvi) Malaka), Kalimantan  Barat  (kecuali  (i) Kapuas Hulu, (ii) Sanggau), Kalimantan Selatan, Kalimantan Tengah, Kalimantan Timur (kecuali (i) Mahakam Hulu, (ii) Berau), Kalimantan  Timur,  dan  Kalimantan  Utara  (kecuali  (i)  Nunukan,  (ii) Malinau.</td>
                                    </tr>
                                    <tr>
                                        <td>Zona 5A</td>
                                        <td>(1) Aceh  Besar,  (2)  Aceh  Singkil,  (3)  Nias,  (4)  Nias  Selatan,  (5)  Nias  Utara,  (6)  Nias  Barat,  (7)  Kep. Mentawai,  (8)  Solok  Selatan,  (9)  Pesisir  Barat,  (10)  Sumba  Barat,  (11)  Sumba  Timur,  (12)  Timor Tengah Selatan, (13) Belu, (14) Alor, (15) Lembata, (16) Ende, (17) Manggarai, (18) Rote Ndao, (19) Manggarai Barat, (20) Sumba  Tengah,  (21) Sumba Barat Daya, (22)  Nagekeo, (23) Manggarai Timur, (24) Sabu Raijua, (25) Malaka, (26) Banggai  Kepulauan,  (27)  Tujo  Una-Una,  (28)  Sigi,  (29)  Banggai Laut, (30) Konawe, (31) Bombana, (32) Konawe Kepulauan, (33) Bengkalis, (34) Kepulauan Meranti, (35) Karimun, (36) Kepulauan Anambas, (37) Natuna, (38) Kapuas Hulu, (39) Mahakam Hulu, (40) Sanggau, (41) Nunukan, (42) Malinau,  (43)  Berau,  (44)  Kepulauan Sangihe, dan (45) Kepulauan Talaud.</td>
                                    </tr>
                                    <tr>
                                        <td>Zona 5B</td>
                                        <td>Papua, Papua Barat, Maluku, dan Maluku Utara</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
              </div>
              <div className="modal fade" id="unduhSemuaModal" data-bs-backdrop="static">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header bg-primary text-white border-0 py-2 px-3">
                      <h6 className="modal-title">
                        <FontAwesomeIcon icon={faCloudDownloadAlt} className="me-2" />
                        Unduh Semua Data
                      </h6>
                      <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body px-3 py-3">
                      <p className="text-muted small mb-3">Pilih jenis buku yang ingin diunduh seluruh datanya:</p>
                      <div className="d-flex flex-column gap-2">
                        <a
                          href="https://bukupusbuk.id/s/het-btu"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <div className="card border-success border-1 rounded-3 p-2 px-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, flexShrink: 0 }}>
                                <FontAwesomeIcon icon={faStar} className="text-success" />
                              </div>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-dark small">BTU</div>
                                <small className="text-muted">Buku Teks Utama — buku acuan wajib yang menjadi sumber utama dalam kurikulum.</small>
                              </div>
                              <FontAwesomeIcon icon={faDownload} className="text-success ms-auto" style={{ flexShrink: 0 }} />
                            </div>
                          </div>
                        </a>
                        <a
                          href="https://bukupusbuk.id/s/het-btp"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <div className="card border-primary border-1 rounded-3 p-2 px-3" style={{ transition: '0.2s' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, flexShrink: 0 }}>
                                <FontAwesomeIcon icon={faBookOpen} className="text-primary" />
                              </div>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-dark small">BTP</div>
                                <small className="text-muted">Buku Teks Pendamping — pelengkap buku teks utama untuk memperkaya materi pembelajaran.</small>
                              </div>
                              <FontAwesomeIcon icon={faDownload} className="text-primary ms-auto" style={{ flexShrink: 0 }} />
                            </div>
                          </div>
                        </a>
                        <a
                          href="https://bukupusbuk.id/s/het-bnt"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <div className="card border-warning border-1 rounded-3 p-2 px-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, flexShrink: 0 }}>
                                <FontAwesomeIcon icon={faBookmark} className="text-warning" />
                              </div>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-dark small">BNT</div>
                                <small className="text-muted">Buku Nonteks — buku pengayaan, referensi, dan panduan di luar buku teks utama.</small>
                              </div>
                              <FontAwesomeIcon icon={faDownload} className="text-warning ms-auto" style={{ flexShrink: 0 }} />
                            </div>
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HasilPenilaian;
